import {useReportErrorContext} from '@github-ui/react-core/future/report-error-context'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useNavigate, useNavigationType, useParams, useSearchParams} from '@github-ui/react-router'
import {Blankslate} from '@primer/react/experimental'
import {useEffect, useEffectEvent, useId, type ReactNode} from 'react'
import {workflowRunsRoute} from './workflow-runs-route'
import {WorkflowRunsListEmptyState} from '../components/workflow-runs/WorkflowRunsList'
import {WorkflowRunsPage} from '../components/workflow-runs/WorkflowRunsPage'
import {removeWorkflowRunsQueryAndPageSearchParams} from '../queries/workflow-runs/workflow-runs-query'
import {OnboardingGuidance} from '../components/OnboardingGuidance'
import {workflowFilePathFromRoute, workflowRouteParam} from '../workflow-path'

export function WorkflowRuns() {
  const params = useRouteParams(workflowRunsRoute)
  const {owner, repo} = params
  const workflowFileName = workflowRouteParam(params)
  const [searchParams] = useSearchParams()
  const workflowPath = workflowFilePathFromRoute(workflowFileName, {lab: searchParams.get('lab') === 'true'})
  const workflowRunsQuery = useRouteQuery(workflowRunsRoute, 'mainQuery', {
    placeholderData: previousData => previousData,
  })
  const resolvedWorkflowName = workflowRunsQuery.data?.title ?? 'Workflow runs'

  const reportError = useReportErrorContext()
  const backgroundError = workflowRunsQuery.data !== undefined ? workflowRunsQuery.error : null
  const reportBackgroundError = useEffectEvent(() => {
    if (backgroundError) reportError(backgroundError, {critical: false})
  })

  useEffect(() => {
    reportBackgroundError()
  }, [backgroundError])

  if (workflowRunsQuery.isError && workflowRunsQuery.data === undefined) throw workflowRunsQuery.error

  if (workflowRunsQuery.data?.workflowExists === false) {
    return <WorkflowNotFound />
  }

  return (
    <>
      {workflowRunsQuery.data !== undefined &&
      !workflowRunsQuery.isPlaceholderData &&
      workflowRunsQuery.data.onboardingGuidance ? (
        <OnboardingGuidance guidance={workflowRunsQuery.data.onboardingGuidance} />
      ) : null}
      <WorkflowRunsPage
        key={`${owner}/${repo}/${workflowPath}/runs`}
        owner={owner}
        repo={repo}
        workflowRunsQueryKey={workflowRunsQuery.queryKey}
        resultsLabel={`Workflow runs for ${resolvedWorkflowName}`}
        workflowRuns={workflowRunsQuery.data?.workflowRuns}
        totalPages={workflowRunsQuery.data?.totalPages}
        totalCount={workflowRunsQuery.data?.totalCount}
        countIsCapped={workflowRunsQuery.data?.countIsCapped}
        pageLimitReached={workflowRunsQuery.data?.pageLimitReached}
        loading={workflowRunsQuery.isFetching}
        showingPreviousResults={workflowRunsQuery.isPlaceholderData}
        selectedWorkflow
        workflowRunActionsEnabled={!workflowRunsQuery.isPlaceholderData}
        aliveChannel={workflowRunsQuery.data?.aliveChannel}
      />
    </>
  )
}

export function WorkflowNotFound({allWorkflowsPath: path}: {allWorkflowsPath?: string}) {
  const routePath = useAllWorkflowsPath()
  const allWorkflowsPath = path ?? routePath
  const navigate = useNavigate()

  return (
    <WorkflowRunsListEmptyState accessibleName="Workflow runs">
      <Blankslate border={false} data-hpc>
        <WorkflowRecoveryHeading>This workflow does not exist.</WorkflowRecoveryHeading>
        <Blankslate.Description>The workflow may have been renamed or deleted.</Blankslate.Description>
        <Blankslate.PrimaryAction onClick={() => navigate(allWorkflowsPath)}>
          View all workflows
        </Blankslate.PrimaryAction>
      </Blankslate>
    </WorkflowRunsListEmptyState>
  )
}

export function WorkflowRunsError() {
  return <WorkflowLoadError />
}

function useAllWorkflowsPath() {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const search = removeWorkflowRunsQueryAndPageSearchParams(searchParams).toString()
  return `/${params.owner ?? ''}/${params.repo ?? ''}/actions${search ? `?${search}` : ''}`
}

export function WorkflowLoadError({
  allWorkflowsPath: path,
  showAllWorkflowsAction = true,
}: {
  allWorkflowsPath?: string
  showAllWorkflowsAction?: boolean
}) {
  const routePath = useAllWorkflowsPath()
  const allWorkflowsPath = path ?? routePath

  return (
    <WorkflowRunsListEmptyState accessibleName="Workflow runs">
      <Blankslate border={false}>
        <WorkflowRecoveryHeading>Unable to load workflow runs</WorkflowRecoveryHeading>
        <Blankslate.Description>
          There was a problem loading workflow runs. Reload the page to try again.
        </Blankslate.Description>
        <Blankslate.PrimaryAction onClick={() => window.location.reload()}>Reload page</Blankslate.PrimaryAction>
        {showAllWorkflowsAction && (
          <Blankslate.SecondaryAction href={allWorkflowsPath}>View all workflows</Blankslate.SecondaryAction>
        )}
      </Blankslate>
    </WorkflowRunsListEmptyState>
  )
}

function WorkflowRecoveryHeading({children}: {children: ReactNode}) {
  const navigationType = useNavigationType()
  const headingId = useId()

  useEffect(() => {
    if (navigationType !== 'POP') document.getElementById(headingId)?.focus()
  }, [headingId, navigationType])

  return (
    <Blankslate.Heading id={headingId} as="h2" data-react-autofocus tabIndex={-1}>
      {children}
    </Blankslate.Heading>
  )
}
